<script setup lang="ts">
/**
 * Home view — chain overview.
 *
 * Orchestrator only: fetches data once, passes it to section components as
 * props. Rendering is delegated to components under web/src/components/home/.
 *
 * The full branch landing page (map, filters, and review search) lives at
 * /branch; home keeps a compact branch-performance ranking scoped to the
 * current ICT month-to-date.
 *
 * bead: lucky-war-ah8.8 — rating trend sparklines, region drill-down
 * bead: lucky-war-sgce.3 — decomposition into focused sections
 */
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import type { OverviewResponse, RankingBranchSummary } from '@/api';
import { fetchOverview, fetchMovers, fetchSalesAnchor, fetchBranchRanking } from '@/api';
import type { SalesAnchorResponse } from '@/api';
import { apiBrand, lensFromQuery, scopeLabel } from '@/lib/brand';
import { mtdMetricWindowParams } from '@/lib/metricWindow';

import LiveCampaignBanner from '@/components/discovery/LiveCampaignBanner.vue';
import ItemSalesWidget from '@/components/ItemSalesWidget.vue';
import SectionCard from '@/components/SectionCard.vue';

import RegionOverviewSection from '@/components/regions/RegionOverviewSection.vue';
import SalesNowCard from '@/components/home/SalesNowCard.vue';
import ChainWowCard from '@/components/home/ChainWowCard.vue';
import DeliveryShareCard from '@/components/home/DeliveryShareCard.vue';
import CustSplitCard from '@/components/common/CustSplitCard.vue';
import BranchRankingTable from '@/components/home/BranchRankingTable.vue';

// ── Stream A + B (Excel replacement) ──────────────────────────────────────────
import TargetPacingCard from '@/components/home/TargetPacingCard.vue';
import AsyncState from '@/components/AsyncState.vue';
import { useAsyncData } from '@/composables/useAsyncData';

const route = useRoute();
const lens = computed(() => lensFromQuery(route.query.brand));

// ── Overview ─────────────────────────────────────────────────────────────────
const overviewState = useAsyncData<OverviewResponse>((signal) => fetchOverview(
  signal,
  apiBrand(lensFromQuery(route.query.brand)),
));
const { data: overview, error: overviewError } = overviewState;
const loadOverview = overviewState.reload;

// ── Derived data from overview (passed to children as props) ────────────────
const regionOptions = computed(() => overview.value?.per_region ?? []);

// ── Sales KPIs (passed to children) ─────────────────────────────────────────
const salesNetTotal = computed(() => overview.value?.sales_net_total ?? null);

// ── Sales anchor (bead lucky-war-wg1p.2) ───────────────────────────────────
// anchor feeds ChainWowCard. Best-effort: on failure we omit rather than fake.
// (The former home__ribbon stat strip was removed 2026-08-17 — it duplicated
// header data in a dim one-liner and carried no signal of its own.)
const moversState = useAsyncData(async (signal) => {
  const brand = apiBrand(lensFromQuery(route.query.brand));
  const [movers, anchor] = await Promise.all([fetchMovers(signal, brand), fetchSalesAnchor(signal, brand)]);
  return { count: movers.items.length, anchor };
});
const { data: moversData, error: moversError } = moversState;
const salesAnchor = computed<SalesAnchorResponse | null>(() => moversData.value?.anchor ?? null);
const loadMoversCount = moversState.reload;

// ── Branch performance (fixed homepage MTD window) ──────────────────────────
// Do not reuse /branch's persisted metric-window selection here. The homepage
// contract is explicit MTD in ICT, while /branch remains user-selectable.
const branchRankingWindow = ref(mtdMetricWindowParams());
const branchRankingState = useAsyncData<RankingBranchSummary[]>((signal) => {
  const window = mtdMetricWindowParams();
  branchRankingWindow.value = window;
  return fetchBranchRanking({
    brand: apiBrand(lensFromQuery(route.query.brand)),
    ...window,
  }, signal);
});
const { data: branchRanking, loading: branchRankingLoading, error: branchRankingError } = branchRankingState;
const loadBranchRanking = branchRankingState.reload;
const branchRankingItems = computed(() => branchRanking.value ?? []);

watch(
  () => route.query.brand,
  () => { loadOverview(); loadMoversCount(); loadBranchRanking(); },
);
</script>

<template>
  <div class="home">
    <!-- ═══ Live-campaign banner (bead lucky-war-kz6) ═══ -->
    <LiveCampaignBanner />

    <main class="content">
      <!-- ═══ Experimental AI banner ═══ -->
      <div class="home__exp-banner" role="alert">
        ข้อมูลเชิงวิเคราะห์ (รีวิวลบ, หัวข้อ, Facebook) มาจาก AI — ใช้ประกอบการตัดสินใจเท่านั้น
      </div>

      <AsyncState v-if="moversError" :loading="false" :error="moversError" :retry="loadMoversCount" />

      <!-- ═══ ยอดขายตอนนี้ — self-contained quick pulse (period + branch
            toggles are local to the card; bead lucky-war-e0yv) ═══ -->
      <SalesNowCard />

      <!-- ═══ Monthly sales vs target — MERGED card (ยอดขายเทียบเป้าหมาย +
           รายละเอียดยอดขายเดือนนี้ folded into one, bead tl5y) + uPlot pace ═══ -->
      <TargetPacingCard />

      <!-- ═══ Chain WoW compare card (matched-day strip + uPlot overlay) ═══ -->
      <ChainWowCard :anchor="salesAnchor" />

      <!-- ═══ Promo and add-on sales — summary stays visible; only the full
           menu ledger discloses inside ItemSalesWidget. ═══ -->
      <SectionCard
        :title="`ยอดขายจากโปรโมชันและรายการเสริม · ${scopeLabel(lens)}`"
        class="home__attach"
      >
        <ItemSalesWidget v-if="salesNetTotal != null" />
      </SectionCard>

      <!-- ═══ Adult/kid POS units — secondary sales detail, collapsed by default ═══ -->
      <CustSplitCard :split="salesAnchor?.cust_split" :collapsed="true" />

      <!-- ═══ Delivery share (own compact card; /branch keeps its own) ═══ -->
      <DeliveryShareCard :brand="apiBrand(lensFromQuery(route.query.brand))" />

      <!-- ═══ Region rollup (drills into /branch; shared with the /branch strip) ═══ -->
      <RegionOverviewSection :region-options="regionOptions" />

      <!-- ═══ Branch performance — fixed current ICT month-to-date window ═══ -->
      <SectionCard
        :title="`ผลการดำเนินงานรายสาขา · ${scopeLabel(lens)}`"
        class="home__branch-ranking"
      >
        <AsyncState
          :loading="branchRankingLoading"
          :error="branchRankingError"
          :empty="!branchRankingLoading && !branchRankingError && branchRankingItems.length === 0"
          :retry="loadBranchRanking"
        >
          <BranchRankingTable
            :items="branchRankingItems"
            :brand="apiBrand(lens)"
            :navigation-window="branchRankingWindow"
            window-label="เดือนนี้"
          />
        </AsyncState>
      </SectionCard>

      <AsyncState v-if="overviewError" :loading="false" :error="overviewError" :retry="loadOverview" />
    </main>
  </div>
</template>

<style scoped>
.home {
  --font-heading: 'Kanit', 'Sarabun', sans-serif;
  display: flex;
  flex-direction: column;
  min-height: 100%;
}

/* ── Experimental AI banner ─────────────────────────────────────────────── */
.home__exp-banner {
  background: var(--degraded-soft);
  border: 1px solid var(--degraded);
  border-radius: var(--radius-chip);
  padding: 0.45rem 0.75rem;
  font-size: 0.68rem;
  color: var(--degraded);
  font-weight: 500;
  margin-bottom: 0.75rem;
  line-height: 1.4;
  text-align: center;
}

/* ── Attach/upsell section: embedded (flattened) child chrome ─────────────
 * The section is ONE card (SectionCard). Children render as sub-sections:
 * their own card chrome is stripped and their h2 headers demoted to small
 * uppercase labels so the hierarchy is card title → sub-label → content. */
.home__attach :deep(.item-sales-widget) {
  margin: 0;
  background: transparent;
  border: none;
  border-radius: 0;
  padding: 0;
  box-shadow: none;
}

/* Empty-state variant of the item widget keeps its dashed calm look. */
.home__attach :deep(.item-sales-widget--empty) {
  border-style: dashed;
  border-color: var(--line-light);
  padding: 0.85rem;
}

/* Sub-labels (were card h2 headers). */
.home__attach :deep(.item-sales-widget__header) {
  font-size: 0.72rem;
  font-weight: 600;
  color: var(--text-dim);
}

/* ── Chain rating footer removed (mgmt feedback 2026-08-13) ─────────────── */
/* ── Filter controls + ranking table moved to /branch (bead etcn.3) ─────── */

</style>
